import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { selectedMerchantStorageKey } from "merchant-portal-frontend/src/global.constants";
import { useLocalStorage } from "merchant-portal-frontend/src/utils/localStorage";

import { useAuth } from "./AuthProvider";

export const unassignedMerchantID = "00000000-0000-0000-0000-000000000000";

export interface MerchantContextValue {
	selectedMerchantID: string | null;
	updateSelectedMerchantID: (selectedMerchantID: string | null) => Promise<void>;
	hasAssignedMerchant: boolean;
	setValidationFunction: (callbackFn: ValidationFunction) => void;
}

export const MerchantContext = React.createContext<MerchantContextValue>({
	selectedMerchantID: null,
	updateSelectedMerchantID: () => Promise.resolve(),
	hasAssignedMerchant: true,
	setValidationFunction: () => {},
});

type ValidationFunction = (merchantID: string | null) => boolean;

const getMerchantIdQueryStr = () => {
	const queryStr = new URLSearchParams(window?.location?.search);
	return queryStr.get("merchantID");
};

// Removes the merchantID param from URL
const removeMerchantIdQueryStr = () => {
	const queryStr = new URLSearchParams(window?.location?.search);
	if (queryStr.has("merchantID")) {
		queryStr.delete("merchantID");
		const queryStrValue = queryStr.toString();
		const fullURL = queryStrValue
			? `${window.location.pathname}?${queryStrValue}`
			: window.location.pathname;
		window.history.replaceState({}, "", fullURL);
	}
};

const MerchantProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { breadToken } = useAuth();

	const validationRef = useRef<ValidationFunction>();

	const merchantIDParam = getMerchantIdQueryStr();

	const assignedMerchant = useMemo(() => breadToken?.claims?.merchantID || "", [breadToken]);
	const hasAssignedMerchant = useMemo(
		() => assignedMerchant !== unassignedMerchantID,
		[assignedMerchant],
	);

	const [storedMerchantID, setStoredSelectedMerchantID] = useLocalStorage<string | null>(
		selectedMerchantStorageKey,
		null,
	);

	useEffect(() => {
		if (merchantIDParam) {
			setStoredSelectedMerchantID(merchantIDParam);
		}
	}, [merchantIDParam, setStoredSelectedMerchantID]);

	const selectedMerchantID = useMemo(() => {
		return merchantIDParam || (hasAssignedMerchant ? assignedMerchant : storedMerchantID);
	}, [assignedMerchant, hasAssignedMerchant, storedMerchantID, merchantIDParam]);

	const updateSelectedMerchantID = useCallback(
		async (newSelectedMerchantID: string | null) => {
			if (merchantIDParam) {
				removeMerchantIdQueryStr();
			}
			if (validationRef.current && !validationRef.current(newSelectedMerchantID)) {
				return;
			}
			if (!hasAssignedMerchant) {
				setStoredSelectedMerchantID(newSelectedMerchantID);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const setValidationFunction = (callbackFn: ValidationFunction) => {
		validationRef.current = callbackFn;
	};

	return (
		<MerchantContext.Provider
			value={{
				selectedMerchantID,
				updateSelectedMerchantID,
				hasAssignedMerchant,
				setValidationFunction,
			}}
		>
			{children}
		</MerchantContext.Provider>
	);
};

export default MerchantProvider;

export const useMerchant = () => {
	const context = useContext(MerchantContext);
	if (context === undefined) {
		throw new Error("useMerchant must be used within a MerchantProvider");
	}
	return context;
};
